import * as R from 'ramda';
import { useEffect, useState } from "react";

import { Share } from "@mui/icons-material";
import { Button, Dialog, TextField } from "@mui/material";
import Masonry from 'react-masonry-css';
import { useNavigate } from "react-router-dom";
import { useWall } from "../App";
import { TextDivider } from "../components";
import Parallax from '../components/Parallax';
import {
  Flex,
  GalleryContainer, Label, LoadingContainer, SkeletonContainer
} from "../style";
import { sizes, utils } from "../variables";

export const getCols = width => {
  if (width <= 600) return 1;
  if (width <= 1024) return 2;
  if (width <= 1500) return 3;
  if (width <= 2000) return 4;
  if (width <= 2500) return 5;
  return 6;
};

export const breaks = {
  default: 4,
  1400: 3,
  1024: 2,
  600: 1,
}

const Gallery = () => {
  const { nfts, walletAddress, setSnackbar, loading, customAddress } =
    useWall();
  const [list, setList] = useState(nfts);
  const [name, setName] = useState("");
  const [parsedName, setParsedName] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [breakpoints, setBreakpoints] = useState(breaks);

  const navigate = useNavigate();

  const parseName = (name) => {
    if (name.includes(" ")) {
      const parsedName = name.split(" ").join("-");
      return parsedName;
    }
    return name;
  };
  useEffect(() => {
    if (!list || !list.length) {
      if (R.isEmpty(walletAddress) || R.isNil(walletAddress)) return navigate('/wall/empty');
      if (!loading) return navigate(`/empty-wall/${walletAddress}`);
    }
  }, [list]);

  useEffect(() => {
    if (!loading) setList(nfts);
  }, [loading]);

  useEffect(() => {
    const parsed = parseName(name);
    setParsedName(parsed);
  }, [name]);

  const validateTitle = (title) => {
    const regex = /^[A-Za-z0-9 _]*[A-Za-z0-9][A-Za-z0-9 _]*$/;
    return regex.test(title) || title === '';
  };

  useEffect(() => {
    window.addEventListener('resize', getBreakpoints);
    return () => window.removeEventListener('resize', getBreakpoints);
  }, []);

  const getBreakpoints = () => {
    if (list && list.length <= 5 && window.innerWidth >= 1501) {
      return setBreakpoints({
        default: list.length
      })
    }
    return setBreakpoints(breaks);
  }

  return (
    <Flex
      width={utils.width.fullVW}
      justifyContent="center"
      alignItems="center"
      backgroundColor="white"
      flexDirection="column"
    >
      <TextDivider>
        <Button variant="text" onClick={() => setShowModal(true)} aria-label="Open modal to create custom shareable link">
          <Label fontFamily="Julius Sans One" fontSize={sizes.lg} className="sharing">
            <Flex justifyContent="center" alignItems="center">
              <span className="highlight">Share your wall</span>
              <Share sx={{ marginLeft: sizes.xs2 }} />
            </Flex>
          </Label>
        </Button>
      </TextDivider>
      <Dialog open={showModal} fullWidth onClose={() => setShowModal(false)}>
        <Flex
          flexDirection="column"
          alignItems="center"
          justifyContent="flex-start"
          p={`${sizes.xl} ${sizes.xl}`}
          gap={sizes.lg}
        >
          <TextDivider>
            <img src="/assets/logo.svg" alt="Wallverse's logomark" />
          </TextDivider>
          <Label
            fontFamily="Julius Sans One"
            fontSize={sizes.lg}
            textAlign="center"
          >
            Give <span className='highlight'>your wall</span> a title
          </Label>

          <TextField
            label="Wall title"
            variant="standard"
            color="primary"
            fullWidth
            value={name}
            onChange={({ target }) => {
              if (validateTitle(target?.value)) setName(target.value);
              else {
                setSnackbar({
                  show: true,
                  content: "Only letters, numbers and spaces allowed!",
                  severity: "warning",
                });
              }
            }}
          ></TextField>
          <TextDivider>
            <Button
              disabled={parsedName === ""}
              color="primary"
              size="large"
              aria-label="Copy shareable link to clipboard"
              onClick={async () => {
                const url = `${process.env.REACT_APP_URL_REDIRECT}/custom/${customAddress}/${parsedName}`;
                try {
                  await window.navigator.clipboard.writeText(url);
                  setSnackbar({
                    show: true,
                    severity: "success",
                    content: "Link copied to clipboard!",
                  });
                  setShowModal(false);
                } catch (error) {
                  setSnackbar({
                    show: true,
                    severity: "warning",
                    content:
                      "We couldn't copy the link to your clipbord! Check permissions",
                  });
                }
              }}
            >
              Get link <Share sx={{ marginLeft: sizes.xs2 }} />
            </Button>
          </TextDivider>
        </Flex>
      </Dialog>
      {(!loading && (
        <GalleryContainer>
          <Masonry
            breakpointCols={breakpoints}
            className="grid"
            columnClassName="grid-column"
          >
            {list &&
              list.map((item) => <Parallax title={item.name} loading="lazy" description={item.description} src={item.image} />)}
          </Masonry>

        </GalleryContainer>
      )) || (
          <LoadingContainer variant="masonry" gap={16} cols={5}>
            {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 0, 1, 2, 3, 4, 5, 6, 7, 8, 9].map(() => (
              <SkeletonContainer
                variant="rectangular"
                height={utils.height.halfVH}
              />
            ))}
          </LoadingContainer>
        )}
    </Flex>
  );
};

export default Gallery;
